import * as React from "react";
// QA-IMPERSONATION
import { ImpersonationBanner } from "./components/QaImpersonation/ImpersonationBanner";
import { useAuth0 } from "@auth0/auth0-react";
import { BrowserRouter as Router, Route, Switch, useLocation, useRouteMatch } from "react-router-dom";
import Fade from "@material-ui/core/Fade";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Home from "./pages";
import PartsPage from "./pages/parts";
import VehicleHistoryPage from "./pages/vehicle-history/[id]";
import { JobViewMemoryProvider } from "./components/VehicleHistory/jobViewMemory";
import { JobRoute } from "./components/JobRoute";
import { useTrackInAppHistory } from "./hooks/useInAppHistory";
import ContactSummaryPage from "./pages/contacts/[id]";
import InvoiceDetailsPage from "./pages/invoices/[id]";
import JobsHistoryPage from "./pages/jobs-history/[id]";
import * as Sentry from "@sentry/react";
import PreferencesPage from "./pages/preferences";
import { InspectionProvider } from "./contexts/inspection-context";
import { PartsOrderingIssuesSurveyProvider } from "./contexts/parts-ordering-issues-survey-context";
import { InspectionProviderV2 } from "./contexts/inspection-context-v2";
import { CloseJobBanner } from "./components/CloseJobBanner/CloseJobBanner";

export const Routes: React.FC<{}> = () => {
  const { user } = useAuth0();
  Sentry.setUser({ username: user.name });
  return (
    <Router>
      {/* QA-IMPERSONATION */}
      <ImpersonationBanner />
      <CloseJobBanner />
      <JobViewMemoryProvider>
        <AnimatedRoutes />
      </JobViewMemoryProvider>
    </Router>
  );
};

/**
 * Cross-fades between sections.
 *
 * Keyed on the first path segment, not the whole pathname, for two reasons: moving
 * between jobs (/jobs/1 -> /jobs/2) shouldn't replay a page transition, and remounting
 * would tear down the inspection providers below along with any in-progress state.
 * The job page itself is still remounted per job on purpose — see the inspection key below.
 *
 * Fade rather than Slide on purpose — Slide animates `transform`, and a transformed
 * ancestor becomes the containing block for `position: sticky`, which would break
 * every sticky header in the app for the duration of the animation.
 */
export const AnimatedRoutes: React.FC<{}> = () => {
  useTrackInAppHistory();
  const location = useLocation();
  const still = useMediaQuery("(prefers-reduced-motion: reduce)");
  const section = `/${location.pathname.split("/")[1] ?? ""}`;
  // The vehicle history links job to job without leaving /jobs. Keying per job remounts the job page and
  // the inspection state, as every other way into a job does: the job form does not reinitialize, so job B
  // would otherwise open holding job A's values and save them onto B, and A's inspection would open on B.
  const jobMatch = useRouteMatch<{ id: string }>("/jobs/:id");
  const inspectionKey = jobMatch ? `job:${jobMatch.params.id}` : section;

  return (
    <Fade in appear key={section} timeout={still ? 0 : 200}>
      <div>
        <Switch location={location}>
          <Route path={"/"} exact component={Home} />
          <Route path={"/parts"} exact component={PartsPage} />
          {/* Gated on ?uiv=vh. Outside the inspection providers: it reads history, never writes one. */}
          <Route path={"/vehicle-history/:id"} exact component={VehicleHistoryPage} />
          <Route path={"/contacts/:id"} component={ContactSummaryPage} />
          <Route path={"/preferences"} exact component={PreferencesPage} />
          <InspectionProvider key={inspectionKey}>
            <InspectionProviderV2>
              <PartsOrderingIssuesSurveyProvider>
                <Route path={"/invoices/:id"} component={InvoiceDetailsPage} />
                <Route path={"/jobs/:id"} component={JobRoute} />
                <Route path={"/jobs-history/:id"} component={JobsHistoryPage} />
              </PartsOrderingIssuesSurveyProvider>
            </InspectionProviderV2>
          </InspectionProvider>
        </Switch>
      </div>
    </Fade>
  );
};
